import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Download, Check, Eye, Edit3, Plus } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { Button } from '@/components/ui/button';
import {
  useBrandProfileQuery,
  useBrandTemplatesQuery,
  usePublishBrandProfileMutation,
  type BrandProfile,
  type BrandTemplate,
  type PublishBrandProfileResult,
} from '@/lib/api/brand-templates';
import { BrandTemplateEditor } from '@/components/settings/brand-template-editor';
import { PdfPreviewIframe } from '@/components/settings/pdf-preview-iframe';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { cn } from '@/lib/utils/cn';

/**
 * Brand & report templates — `/settings/brand` (T-0112 / C20-122, E05-F02).
 *
 * Per `design_handoff_caliber_v2/README.md` §4 this is the second of the
 * two **load-bearing** firm-settings screens (the README bolds "Brand &
 * report templates"). Mirrors the prototype's `screens-firm.js →
 * firmBrand()`:
 *
 *   - Left: live PDF cover preview that re-renders as the editor fields
 *     change (200ms debounce).
 *   - Right: stacked editors — brand assets, color tokens, header /
 *     footer text, cover layout & type pairing.
 *   - Below: template registry data-grid.
 *
 * Backend gap (flagged in `brand-templates.ts`): there is no firm-admin
 * CRUD surface for the brand profile or template versions today. T-0059
 * reads `brand_template_version` from the engagement context but the
 * profile itself is configured via App Configuration. The screen reads
 * mock data + writes via a stub mutation that returns `{ updated: 1 }`.
 * When the backend lands, swap the call sites in `brand-templates.ts`.
 *
 * Re-render contract (per ADR-009): publishing brand changes does NOT
 * retroactively change captured snapshots — frozen artifacts keep the
 * brand profile they were captured under. The alert at the bottom of the
 * screen surfaces this.
 */
export const Route = createFileRoute('/settings/brand')({
  component: BrandSettings,
});

function BrandSettings() {
  const profileQuery = useBrandProfileQuery();
  const templatesQuery = useBrandTemplatesQuery();
  const publishMutation = usePublishBrandProfileMutation();

  // Local draft updated on every editor change; the preview reads from
  // this directly so changes feel live (with the editor's own 200ms
  // debounce baked into the preview component).
  const [draft, setDraft] = useState<BrandProfile | null>(null);
  // Last-saved affordance — surfaces "Saved as vX.Y" per the prototype
  // copy. The backend mints a new version row on every PUT so the label
  // always advances after a successful publish.
  const [lastPublished, setLastPublished] = useState<PublishBrandProfileResult | null>(null);
  useEffect(() => {
    if (profileQuery.data) {
      setDraft(profileQuery.data);
    }
  }, [profileQuery.data]);

  if (profileQuery.isLoading || templatesQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading brand profile…
      </div>
    );
  }
  if (profileQuery.error) {
    return <ErrorMessage error={profileQuery.error} />;
  }
  if (templatesQuery.error) {
    return <ErrorMessage error={templatesQuery.error} />;
  }
  if (!profileQuery.data || !templatesQuery.data || !draft) {
    return <p className="text-body-sm text-ink-500">No brand profile available.</p>;
  }

  const handlePublish = () => {
    publishMutation.mutate(draft, {
      onSuccess: (result) => {
        setLastPublished(result);
      },
    });
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(profileQuery.data);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="t-h1 font-semibold text-ink-900">Brand &amp; report templates</h1>
          <p className="t-meta text-ink-500">
            1 brand profile · {templatesQuery.data.length} templates · changes apply to new reports
            only
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline">
            <Download className="size-3.5" aria-hidden="true" /> Export brand kit
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handlePublish}
            disabled={!dirty || publishMutation.isPending}
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700 disabled:bg-ink-100 disabled:text-ink-500"
          >
            <Check className="size-3.5" aria-hidden="true" />{' '}
            {publishMutation.isPending ? 'Publishing…' : 'Publish changes'}
          </Button>
        </div>
      </header>

      {publishMutation.error ? <ErrorMessage error={publishMutation.error as Error} /> : null}

      {lastPublished && !dirty && !publishMutation.isPending ? (
        <div className="flex items-center gap-2 rounded-card border border-posture-healthy-tint bg-posture-healthy-tint/40 px-3 py-2 text-body-sm text-posture-healthy">
          <Check className="size-4" aria-hidden="true" />
          Saved as <span className="font-mono font-semibold">{lastPublished.version}</span> ·
          previous versions stay intact for frozen reports.
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <section
          aria-labelledby="brand-preview-heading"
          className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-card-pad py-3">
            <div>
              <h2 id="brand-preview-heading" className="text-h3 font-semibold text-ink-900">
                Cover page preview
              </h2>
              <p className="text-meta text-ink-500">
                Pulse report · Brightwater · 10 May 2026 · re-renders as fields change
              </p>
            </div>
          </div>
          <div className="bg-paper-50 p-6">
            <PdfPreviewIframe profile={draft} />
            <p className="mt-3 text-center text-meta text-ink-500">
              Rendered to PDF with each report · about 340 KB
            </p>
          </div>
        </section>

        <BrandTemplateEditor profile={draft} onChange={setDraft} />
      </div>

      <TemplateRegistry templates={templatesQuery.data} />

      <div className="rounded-card border border-ai-surface-border bg-ai-surface p-3 text-body-sm text-ai-ink">
        <strong className="font-semibold">Re-render contract.</strong> Publishing brand changes does{' '}
        <strong>not</strong> retroactively change captured snapshots — frozen artifacts keep the
        brand profile they were captured under. New exports use the latest published profile. The
        audit trail records every publish under <em>Firm settings → brand</em>.
      </div>
    </div>
  );
}

interface TemplateRegistryProps {
  templates: BrandTemplate[];
}

function TemplateRegistry({ templates }: TemplateRegistryProps) {
  const columns: DataGridColumn<BrandTemplate>[] = [
    {
      key: 'name',
      header: 'Template',
      cell: (r) => (
        <div>
          <div className="font-medium text-ink-900">{r.name}</div>
          <div className="font-mono text-meta text-ink-500">
            {r.id} · {r.used}
          </div>
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      cell: (r) => (
        <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700">
          {r.kind}
        </span>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      cellClassName: 'font-mono',
      cell: (r) => r.version,
    },
    {
      key: 'updated',
      header: 'Last updated',
      cellClassName: 'text-ink-500',
      cell: (r) => r.updated_at,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) =>
        r.status === 'Draft' ? (
          <span className="inline-flex h-[22px] items-center rounded-full bg-accent-amber-50 px-2.5 text-meta font-medium text-accent-amber-700">
            Draft
          </span>
        ) : (
          <span className="inline-flex h-[22px] items-center rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy">
            Published
          </span>
        ),
    },
    {
      key: 'usage',
      header: 'Usage',
      cellClassName: 'text-ink-500',
      cell: (r) => `${r.used_in} engagement${r.used_in === 1 ? '' : 's'}`,
    },
    {
      key: 'actions',
      header: '',
      cell: () => (
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" variant="outline">
            <Eye className="size-3" aria-hidden="true" /> Preview
          </Button>
          <Button type="button" size="sm" variant="outline">
            <Edit3 className="size-3" aria-hidden="true" /> Edit
          </Button>
        </div>
      ),
    },
  ];
  return (
    <section
      aria-labelledby="template-registry-heading"
      className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
    >
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-card-pad py-3',
        )}
      >
        <div>
          <h2 id="template-registry-heading" className="text-h3 font-semibold text-ink-900">
            Report templates
          </h2>
          <p className="text-meta text-ink-500">
            {templates.length} templates bound to engagement workflows
          </p>
        </div>
        <Button type="button" size="sm" variant="outline">
          <Plus className="size-3.5" aria-hidden="true" /> New template
        </Button>
      </div>
      <DataGrid columns={columns} rows={templates} rowKey={(r) => r.id} />
    </section>
  );
}
